/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Radio,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import {
  ReactHookFormDateTime,
  ReactHookFormFormControl,
  ReactHookFormNumberField,
  ReactHookFormProvider,
  ReactHookFormRadioGroup,
  ReactHookFormSelect,
  ReactHookFormTextField,
  defaultFormOptions,
  isLatitude,
  isLongitude,
  isValidMax,
  isValidMin,
  isXHoursAfter,
  isXHoursBefore,
} from '@opengeoweb/form-fields';
import {
  AlertBanner,
  dateUtils,
  usePreventBrowserClose,
} from '@opengeoweb/shared';
import { usePublishProduct } from '../../api/hooks';
import { useKNMIComponentsTranslation } from '../../utils/i18n';

const formStyles = {
  padding: '32px 16px',
  '.MuiSelect-select.MuiInputBase-input.MuiFilledInput-input': {
    paddingTop: '17px',
    paddingBottom: '0px',
  },
  '.MuiInputBase-input.MuiFilledInput-input': {
    paddingTop: '15px',
    paddingBottom: '2px',
  },
  '.MuiPickersSectionList-root': {
    paddingTop: '17px',
    paddingBottom: '0px',
  },
};
const rowStyles = {
  width: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  marginTop: '-8px',
};
const fieldStyles = {
  flexGrow: 1,
  flexBasis: '240px',
  minWidth: '240px',
};
const fieldStylesRadio = {
  flexGrow: 1,
  flexBasis: '112px',
  minWidth: '112px',
};

const MIN_HEIGHT = 0;
const MAX_HEIGHT = 10000;
const MIN_DURATION = 1;
const MAX_DURATION = 125;
const MIN_MAX_TIME = 120;

// String type for number fields is to allow empty default values
export interface TrajksFormValues {
  referenceName: string;
  lat: number | string;
  lon: number | string;
  releaseHeight: number | string;
  start: string;
  direction: string;
  duration: number;
  numVerticalLevels: number;
  spatialUncertainty: boolean;
}

export const generateDefaultValues = (): TrajksFormValues => {
  return {
    referenceName: '',
    lat: '',
    lon: '',
    releaseHeight: '',
    start: dateUtils.dateToString(dateUtils.sub(dateUtils.utc(), {})) ?? '',
    direction: 'forward',
    duration: 48,
    numVerticalLevels: 1,
    spatialUncertainty: false,
  };
};

export const Form = (): React.JSX.Element => {
  const { t } = useKNMIComponentsTranslation();
  const { handleSubmit, formState, reset, watch, setValue } = useFormContext();
  const { dirtyFields, isValid } = formState;
  const isDirty = Object.keys(dirtyFields).length > 0;

  usePreventBrowserClose(isDirty);

  const {
    mutate: publishProduct,
    error: publishError,
    isPending: isPublishing,
  } = usePublishProduct();

  const handleSendForm = (): void => {
    void handleSubmit((data): void => {
      publishProduct({
        data: data as TrajksFormValues,
        clearForm: handleClearForm,
      });
    })();
  };

  const handleClearForm = (): void => {
    reset(generateDefaultValues());
  };

  const spatialUncertainty = watch('spatialUncertainty');
  return (
    <form
      data-testid="trajks-form"
      style={{ height: '100%', overflowY: 'auto' }}
    >
      {isPublishing ? (
        <LinearProgress
          color="secondary"
          sx={{ position: 'absolute', width: '100%' }}
        />
      ) : null}
      {publishError ? (
        <Box sx={{ width: '100%' }}>
          <AlertBanner
            info={publishError.message}
            shouldClose
            title={t('error-publish')}
          />
        </Box>
      ) : null}
      <Grid container spacing={3} sx={formStyles}>
        <ReactHookFormTextField
          label={t('reference-name')}
          name="referenceName"
          rules={{ required: true }}
          size="small"
        />

        <Box sx={rowStyles}>
          <Box sx={fieldStyles}>
            <ReactHookFormNumberField
              inputMode="decimal"
              label={t('latitude')}
              name="lat"
              rules={{
                required: true,
                validate: {
                  isLatitude,
                },
              }}
              size="small"
            />
          </Box>

          <Box sx={fieldStyles}>
            <ReactHookFormNumberField
              inputMode="decimal"
              label={t('longitude')}
              name="lon"
              rules={{
                required: true,
                validate: {
                  isLongitude,
                },
              }}
              size="small"
            />
          </Box>
        </Box>
        <Box sx={rowStyles}>
          <Box sx={fieldStyles}>
            <ReactHookFormNumberField
              label={t('release-height')}
              name="releaseHeight"
              rules={{
                required: true,
                validate: {
                  maxHeight: (value): boolean | string =>
                    isValidMax(value, MAX_HEIGHT) ||
                    t('release-height-max-error', {
                      maxValue: MAX_HEIGHT,
                    }),

                  minHeight: (value): boolean | string =>
                    isValidMin(value, MIN_HEIGHT) ||
                    t('release-height-min-error', {
                      minHeightValue: MIN_HEIGHT,
                    }),
                },
              }}
              size="small"
            />
          </Box>
          <Box sx={fieldStyles}>
            <ReactHookFormDateTime
              format="dd/MM/yyyy HH:mm"
              label={t('start')}
              name="start"
              openTo="hours"
              rules={{
                required: true,
                validate: {
                  isValidDate: dateUtils.isValidIsoDateString,

                  isCurrentTimeWithinXHoursValue: (value): boolean | string => {
                    const isValidBefore = isXHoursBefore(
                      dateUtils.getCurrentTimeAsString(),
                      value,
                      MIN_MAX_TIME,
                    );
                    if (!isValidBefore) {
                      return t('start-time-after-error-message', {
                        hours: MIN_MAX_TIME,
                      });
                    }
                    const isValidAfter = isXHoursAfter(
                      dateUtils.getCurrentTimeAsString(),
                      value,
                      MIN_MAX_TIME,
                    );
                    return (
                      (isValidBefore && isValidAfter) ||
                      t('start-time-before-error-message', {
                        hours: MIN_MAX_TIME,
                      })
                    );
                  },
                },
              }}
            />
          </Box>
        </Box>
        <ReactHookFormRadioGroup
          name="direction"
          row
          rules={{ required: true }}
        >
          <Box sx={rowStyles}>
            <Box sx={fieldStylesRadio}>
              <FormControlLabel
                control={<Radio />}
                key="forward"
                label={t('direction-for')}
                value="forward"
              />
            </Box>
            <Box sx={fieldStylesRadio}>
              <FormControlLabel
                control={<Radio />}
                key="backward"
                label={t('direction-back')}
                value="backward"
              />
            </Box>
          </Box>
        </ReactHookFormRadioGroup>
        <Box sx={rowStyles}>
          <Box sx={fieldStyles}>
            <ReactHookFormNumberField
              label={t('trajectory-duration')}
              name="duration"
              rules={{
                required: true,
                validate: {
                  maxDuration: (value): boolean | string =>
                    isValidMax(value, MAX_DURATION) ||
                    t('trajectory-duration-max-error', {
                      maxValue: MAX_DURATION,
                    }),

                  minDuration: (value): boolean | string =>
                    // The min level depends on the unit
                    isValidMin(value, MIN_DURATION) ||
                    t('trajectory-duration-min-error', {
                      minLevelValue: MIN_DURATION,
                    }),
                },
              }}
              size="small"
            />
          </Box>

          <Box sx={fieldStyles}>
            <ReactHookFormSelect
              label={t('num-vertical-levels')}
              name="numVerticalLevels"
              rules={{
                required: true,
              }}
              size="small"
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((level) => {
                return (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                );
              })}
            </ReactHookFormSelect>
          </Box>
        </Box>
        <ReactHookFormFormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={spatialUncertainty}
                color="secondary"
                name="spatialUncertainty"
                onChange={(): void => {
                  setValue('spatialUncertainty', !spatialUncertainty, {
                    shouldDirty: true,
                  });
                }}
              />
            }
            label={t('spatial-uncertainty')}
          />
        </ReactHookFormFormControl>
        <Grid
          alignItems="center"
          container
          justifyContent="flex-end"
          size={12}
          spacing={2}
        >
          <Grid>
            <Button
              onClick={handleClearForm}
              size="small"
              sx={{ width: '136px', height: '32px' }}
              variant="tertiary"
            >
              {t('clear-form')}
            </Button>
          </Grid>
          <Grid>
            <Button
              disabled={!isValid || isPublishing}
              onClick={handleSendForm}
              size="small"
              sx={{ width: '136px', height: '32px' }}
              variant="primary"
            >
              {t('publish-form')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export const TrajksForm: React.FC = (props) => {
  return (
    <ReactHookFormProvider
      options={{
        ...defaultFormOptions,
        defaultValues: generateDefaultValues(),
      }}
    >
      <Form {...props} />
    </ReactHookFormProvider>
  );
};
